import type {
    SupplierDetailsItem,
    SupplierPaymentMethod,
    SupplierStatementRow,
} from '@/api/models';
import { formatGel } from '@/utils/money';

/**
 * Presentational config + row shape rendered by the Suppliers table.
 *
 * The row shape is the bos-api's `SupplierDetailsItem` verbatim — the table is
 * wired to `useSupplierDetailsQuery` and surfaces every field it returns.
 */

export type Supplier = SupplierDetailsItem;

export type SupplierRowAction =
    'view' | 'payment' | 'edit' | 'archive' | 'unarchive' | 'delete';

/** Icon shown in the thumbnail cell when a supplier has no image. */
export const NO_IMAGE_ICON = 'carbon:no-image';

/** Stable column ids — shared between the View columns and sort descriptor. */
export const SUPPLIER_COLUMN = {
    image: 'image',
    name: 'name',
    serialNumber: 'serialNumber',
    phone: 'phone',
    email: 'email',
    balance: 'balance',
    actions: 'actions',
} as const;

export const SUPPLIER_ROW_ACTIONS: {
    id: SupplierRowAction;
    icon: string;
    labelKey: string;
    variant?: 'danger';
    /**
     * Whether this action belongs on an archived row (`true`), a live one
     * (`false`), or either (`undefined`).
     */
    onlyWhenArchived?: boolean;
}[] = [
    {
        id: 'view',
        icon: 'gravity-ui:file-text',
        labelKey: 'suppliers.components.supplierTable.actions.view',
    },
    {
        id: 'payment',
        icon: 'gravity-ui:credit-card',
        labelKey: 'suppliers.components.supplierTable.actions.payment',
    },
    {
        id: 'edit',
        icon: 'gravity-ui:pencil',
        labelKey: 'suppliers.components.supplierTable.actions.edit',
    },
    {
        id: 'archive',
        icon: 'gravity-ui:box',
        labelKey: 'suppliers.components.supplierTable.actions.archive',
        // Archiving and unarchiving are the same slot: a supplier is in one
        // state or the other, so offering both would always offer one no-op.
        onlyWhenArchived: false,
    },
    {
        id: 'unarchive',
        icon: 'gravity-ui:arrow-up-from-square',
        labelKey: 'suppliers.components.supplierTable.actions.unarchive',
        onlyWhenArchived: true,
    },
    {
        id: 'delete',
        icon: 'gravity-ui:trash-bin',
        labelKey: 'suppliers.components.supplierTable.actions.delete',
        variant: 'danger',
    },
];

export type BalanceDisplay = {
    text: string;
    variant: 'neutral' | 'success' | 'danger';
};

/**
 * The "of which N on account" caption that sits under a supplier's balance
 * (BOS-161), or `null` when there is nothing on account to explain.
 *
 * Returned as text rather than decided in the View, because "is there anything
 * unallocated?" is a question about the account, not about layout. When it is
 * non-null the reader can do the arithmetic that reconciles this page against
 * Purchases; when it is null the two agree already and the caption would be
 * noise on every row.
 */
export const formatUnallocated = (
    unallocatedTotalTetri: number,
    label: string,
): string | null => {
    if (unallocatedTotalTetri <= 0) {
        return null;
    }
    return `${label}: ${formatGel(unallocatedTotalTetri)}`;
};

/**
 * Render the supplier balance for the table cell. Mapping:
 * - `0`     → localized "Fully paid", neutral colour
 * - `> 0`   → "+N CURRENCY" — we have a prepayment credit, success colour
 * - `< 0`   → "-N CURRENCY" — we owe the supplier, danger colour
 */
export const formatBalance = (
    balanceTetri: number,
    fullyPaidLabel: string,
): BalanceDisplay => {
    if (balanceTetri === 0) {
        return { text: fullyPaidLabel, variant: 'neutral' };
    }
    // `formatGel` already prefixes a minus; the sign is prepended to the
    // magnitude here so a credit reads `+₾ 12.00` rather than `₾ 12.00`.
    const magnitude = formatGel(Math.abs(balanceTetri));
    return balanceTetri > 0
        ? { text: `+${magnitude}`, variant: 'success' }
        : { text: `-${magnitude}`, variant: 'danger' };
};

/** Default rows shown per page in the suppliers table. */
export const SUPPLIERS_PAGE_SIZE = 10;

/** Selectable page sizes for the suppliers table's rows-per-page control. */
export const SUPPLIERS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

/**
 * Compute the page numbers to render in a pagination control, collapsing long
 * ranges with `'ellipsis'` markers (first/last always shown, plus a window
 * around the current page). Pure — unit-tested directly.
 */
export const getPaginationWindow = (
    totalPages: number,
    current: number,
): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i += 1) {
            pages.push(i);
        }
        return pages;
    }
    pages.push(1);
    if (current > 3) {
        pages.push('ellipsis');
    }
    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);
    for (let i = start; i <= end; i += 1) {
        pages.push(i);
    }
    if (current < totalPages - 2) {
        pages.push('ellipsis');
    }
    pages.push(totalPages);
    return pages;
};

/**
 * Render a signed GEL amount with an explicit sign, for the statement and the
 * account header. Unlike {@link formatBalance} there is no "settled" wording:
 * a zero movement is still a number in a column of numbers.
 */
export const formatSignedAmount = (amountTetri: number): string =>
    `${amountTetri < 0 ? '-' : '+'}${formatGel(Math.abs(amountTetri))}`;

/** Render an unsigned GEL magnitude — owed and paid totals, invoice amounts. */
export const formatAmount = (amountTetri: number): string =>
    formatGel(amountTetri);

/**
 * How much of an invoice is still outstanding: what the supplier is owed for it
 * minus what has been allocated to it. Clamped at zero — an over-allocation is
 * a supplier-level prepayment, not a negative outstanding on one document.
 *
 * The first argument is `payableTotal`, not `totalAmount`: the landed total is
 * what the stock cost, VAT excluded and third-party freight included, and
 * settling an invoice against it left the supplier's balance disagreeing with
 * every document on it (bos-api BOS-125).
 */
export const outstandingOf = (
    payableTotalTetri: number,
    paidAmountTetri: number,
): number => Math.max(payableTotalTetri - paidAmountTetri, 0);

/** Colour bucket for a statement row's amount: an invoice owes, a payment pays. */
export const statementRowVariant = (
    row: SupplierStatementRow,
): 'success' | 'danger' => (row.amount < 0 ? 'danger' : 'success');

/** i18n key for a payment method's label. */
export const paymentMethodLabelKey = (method: SupplierPaymentMethod): string =>
    `suppliers.components.supplierPaymentModal.methods.${method}`;

/** Default rows shown per page in the supplier payments list. */
export const SUPPLIER_PAYMENTS_PAGE_SIZE = 20;

/** The row actions that apply to a supplier in the given state. */
export const supplierRowActionsFor = (
    archived: boolean,
): typeof SUPPLIER_ROW_ACTIONS =>
    SUPPLIER_ROW_ACTIONS.filter(
        (action) =>
            action.onlyWhenArchived === undefined ||
            action.onlyWhenArchived === archived,
    );
